import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useQuery } from '@tanstack/react-query';
import { X, Funnel, MagnifyingGlass, CaretDown, CalendarBlank } from '@phosphor-icons/react';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { listTeams } from '@/api/teams';
import { listInvoiceTypes } from '@/api/billing';
import { resolve as resolveTeamColor } from '@/lib/teamColor';

// ── Filter shape ────────────────────────────────────────────────────────────

// Warning keys map 1-to-1 to API params. Each selected key narrows the result
// (AND logic). Selecting 'no_email' + 'no_phone' shows members missing both.
export type WarningKey = 'no_photo' | 'no_email' | 'no_phone';

export type PaymentStatusFilter = '' | 'paid' | 'unpaid' | 'none';

export interface MemberFilters {
  teamIds: string[];
  personalIdStatuses: ('none' | 'valid' | 'needs_review')[];
  warnings: WarningKey[];
  isLeader: boolean;
  // Pure guardians (people whose only relationship to the org is being a
  // guardian of another member) are hidden by default — they aren't team
  // participants and clutter the roster view. Flip this to surface them.
  includeGuardians: boolean;
  createdAfter: string;  // YYYY-MM-DD, '' = not set
  createdBefore: string; // YYYY-MM-DD, '' = not set
  // Billing filter (PR2). paymentStatus '' = off. costTypeId '' = the org's
  // membership-fee type (the common case); set it to filter a specific type.
  paymentStatus: PaymentStatusFilter;
  costTypeId: string;
}

export const EMPTY_FILTERS: MemberFilters = {
  teamIds: [],
  personalIdStatuses: [],
  warnings: [],
  isLeader: false,
  includeGuardians: false,
  createdAfter: '',
  createdBefore: '',
  paymentStatus: '',
  costTypeId: '',
};

export function countActiveFilters(f: MemberFilters): number {
  let n = 0;
  if (f.teamIds.length > 0) n++;
  if (f.personalIdStatuses.length > 0) n++;
  if (f.warnings.length > 0) n++;
  if (f.isLeader) n++;
  if (f.includeGuardians) n++;
  if (f.createdAfter) n++;
  if (f.createdBefore) n++;
  if (f.paymentStatus) n++;
  return n;
}

// Map filter warnings to the API params expected by listMembers.
export function warningsToApiParams(warnings: WarningKey[]): {
  hasPhoto?: boolean;
  hasEmail?: boolean;
  hasPhone?: boolean;
  hasContactIssue?: boolean;
} {
  const p: { hasPhoto?: boolean; hasEmail?: boolean; hasPhone?: boolean } = {};
  if (warnings.includes('no_photo')) p.hasPhoto = false;
  if (warnings.includes('no_email')) p.hasEmail = false;
  if (warnings.includes('no_phone')) p.hasPhone = false;
  return p;
}

// ── Panel ────────────────────────────────────────────────────────────────────

interface Props {
  slug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: MemberFilters;
  onChange: (f: MemberFilters) => void;
}

export function MemberFilterPanel({ slug, open, onOpenChange, filters, onChange }: Props) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const activeCount = countActiveFilters(filters);

  const teamsQuery = useQuery({
    queryKey: ['teams', slug],
    queryFn: () => listTeams(token, slug),
    enabled: !!token && !!slug && open,
  });

  const invoiceTypesQuery = useQuery({
    queryKey: ['billing-invoice-types', slug, false],
    queryFn: () => listInvoiceTypes(token, slug, false),
    enabled: !!token && !!slug && open,
  });

  function setPaymentStatus(status: PaymentStatusFilter) {
    onChange({ ...filters, paymentStatus: filters.paymentStatus === status ? '' : status });
  }

  function toggleTeam(id: string) {
    const next = filters.teamIds.includes(id)
      ? filters.teamIds.filter((x) => x !== id)
      : [...filters.teamIds, id];
    onChange({ ...filters, teamIds: next });
  }

  function togglePersonalIdStatus(status: 'none' | 'valid' | 'needs_review') {
    const next = filters.personalIdStatuses.includes(status)
      ? filters.personalIdStatuses.filter((s) => s !== status)
      : [...filters.personalIdStatuses, status];
    onChange({ ...filters, personalIdStatuses: next });
  }

  function toggleWarning(key: WarningKey) {
    const next = filters.warnings.includes(key)
      ? filters.warnings.filter((w) => w !== key)
      : [...filters.warnings, key];
    onChange({ ...filters, warnings: next });
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-[22rem]"
      >
        {/* ── Header ── */}
        <div className="flex shrink-0 items-center gap-2 border-b border-border px-5 py-4">
          <Funnel className="h-4 w-4 text-muted-foreground" />
          <SheetTitle className="font-heading text-base font-bold">
            {t('members.filter.title')}
          </SheetTitle>
          {activeCount > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-pill bg-primary px-1.5 font-heading text-[0.65rem] font-bold text-primary-foreground">
              {activeCount}
            </span>
          )}
          <SheetDescription className="sr-only">
            {t('members.filter.description')}
          </SheetDescription>
        </div>

        {/* ── Scrollable sections ── */}
        <div className="flex-1 divide-y divide-border overflow-y-auto">

          {/* Teams */}
          <FilterSection title={t('members.filter.teams.title')}>
            <TeamsCombobox
              teams={teamsQuery.data?.teams ?? []}
              loading={teamsQuery.isLoading}
              selectedIds={filters.teamIds}
              onToggle={toggleTeam}
              onClearAll={() => onChange({ ...filters, teamIds: [] })}
            />
          </FilterSection>

          {/* Personal ID */}
          <FilterSection title={t('members.filter.personalId.title')}>
            <div className="flex flex-wrap gap-2">
              {(['none', 'valid', 'needs_review'] as const).map((status) => (
                <PillToggle
                  key={status}
                  active={filters.personalIdStatuses.includes(status)}
                  onClick={() => togglePersonalIdStatus(status)}
                >
                  {t(`members.filter.personalId.${status}`)}
                </PillToggle>
              ))}
            </div>
            {filters.personalIdStatuses.length > 0 && (
              <ClearLink onClick={() => onChange({ ...filters, personalIdStatuses: [] })} />
            )}
          </FilterSection>

          {/* Warnings */}
          <FilterSection title={t('members.filter.warnings.title')}>
            <WarningsCombobox
              selectedKeys={filters.warnings}
              onToggle={toggleWarning}
              onClearAll={() => onChange({ ...filters, warnings: [] })}
            />
          </FilterSection>

          {/* Role */}
          <FilterSection title={t('members.filter.role.title')}>
            <div className="flex flex-wrap gap-2">
              <PillToggle
                active={filters.isLeader}
                onClick={() => onChange({ ...filters, isLeader: !filters.isLeader })}
              >
                {t('members.filter.role.leader')}
              </PillToggle>
              <PillToggle
                active={filters.includeGuardians}
                onClick={() =>
                  onChange({ ...filters, includeGuardians: !filters.includeGuardians })
                }
              >
                {t('members.filter.role.showGuardians')}
              </PillToggle>
            </div>
            {(filters.isLeader || filters.includeGuardians) && (
              <ClearLink
                onClick={() =>
                  onChange({ ...filters, isLeader: false, includeGuardians: false })
                }
              />
            )}
          </FilterSection>

          {/* Member since */}
          <FilterSection title={t('members.filter.memberSince.title')}>
            <div className="flex flex-col gap-3">
              <DateField
                label={t('members.filter.memberSince.from')}
                value={filters.createdAfter}
                max={filters.createdBefore || undefined}
                onChange={(v) => onChange({ ...filters, createdAfter: v })}
              />
              <DateField
                label={t('members.filter.memberSince.to')}
                value={filters.createdBefore}
                min={filters.createdAfter || undefined}
                onChange={(v) => onChange({ ...filters, createdBefore: v })}
              />
            </div>
          </FilterSection>

          {/* Billing (membership / cost-type payment status) */}
          <FilterSection title={t('members.filter.billing.title')}>
            <div className="flex flex-wrap gap-2">
              {(['paid', 'unpaid', 'none'] as const).map((status) => (
                <PillToggle
                  key={status}
                  active={filters.paymentStatus === status}
                  onClick={() => setPaymentStatus(status)}
                >
                  {t(`members.filter.billing.${status}`)}
                </PillToggle>
              ))}
            </div>
            <div className="mt-3 flex flex-col gap-1.5 text-sm">
              <span className="text-muted-foreground">{t('members.filter.billing.costType')}</span>
              {/* '' (= the membership fee itself) can't be a Radix item value,
                  so it travels as the __membership__ sentinel. */}
              <Select
                value={filters.costTypeId === '' ? '__membership__' : filters.costTypeId}
                onValueChange={(v) =>
                  onChange({ ...filters, costTypeId: v === '__membership__' ? '' : v })
                }
              >
                <SelectTrigger aria-label={t('members.filter.billing.costType')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__membership__">
                    {t('members.filter.billing.costTypeMembership')}
                  </SelectItem>
                  {(invoiceTypesQuery.data ?? []).map((it) => (
                    <SelectItem key={it.id} value={it.id}>
                      {it.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {filters.paymentStatus && (
              <ClearLink onClick={() => onChange({ ...filters, paymentStatus: '', costTypeId: '' })} />
            )}
          </FilterSection>
        </div>

        {/* ── Footer ── */}
        {activeCount > 0 && (
          <div className="shrink-0 border-t border-border px-5 py-4">
            <button
              type="button"
              onClick={() => onChange(EMPTY_FILTERS)}
              className="w-full rounded-pill border border-border py-2 font-heading text-sm font-semibold text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('members.filter.clearAll')}
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

function FilterSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3 px-5 py-4">
      <h3 className="font-heading text-[0.7rem] font-bold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      {children}
    </div>
  );
}

function PillToggle({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded-pill border px-3 py-1.5 font-heading text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function ClearLink({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation('common');
  return (
    <button
      type="button"
      onClick={onClick}
      className="self-start font-heading text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none"
    >
      {t('members.filter.clearSection')}
    </button>
  );
}

function DateField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: string;
  min?: string | undefined;
  max?: string | undefined;
  onChange: (v: string) => void;
}) {
  const { t, i18n } = useTranslation('common');
  const [open, setOpen] = useState(false);

  const displayValue = value
    ? new Intl.DateTimeFormat(i18n.language, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }).format(new Date(value + 'T00:00:00'))
    : t('members.filter.memberSince.pick');

  return (
    <div className="flex flex-col gap-1.5">
      <span className="font-heading text-xs font-semibold text-muted-foreground">{label}</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              value
                ? 'border-primary bg-primary/10 font-heading font-semibold text-foreground'
                : 'border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground',
            )}
          >
            <CalendarBlank className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="flex-1 text-left">{displayValue}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-2" side="bottom" sideOffset={4}>
          <Calendar
            value={value || undefined}
            onSelect={(d) => { onChange(d); setOpen(false); }}
            min={min}
            max={max}
            locale={i18n.language}
          />
        </PopoverContent>
      </Popover>
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="self-start font-heading text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none"
        >
          <X className="mr-0.5 inline h-3 w-3" />
          {t('members.filter.clearSection')}
        </button>
      )}
    </div>
  );
}

function WarningsCombobox({
  selectedKeys,
  onToggle,
  onClearAll,
}: {
  selectedKeys: WarningKey[];
  onToggle: (key: WarningKey) => void;
  onClearAll: () => void;
}) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);

  const label =
    selectedKeys.length === 0
      ? t('members.filter.warnings.all')
      : t('members.filter.warnings.selected', { count: selectedKeys.length });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex w-full items-center justify-between rounded-lg border px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            selectedKeys.length > 0
              ? 'border-primary bg-primary/10 font-heading font-semibold text-foreground'
              : 'border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground',
          )}
        >
          <span>{label}</span>
          <CaretDown
            className={cn('h-3.5 w-3.5 shrink-0 transition-transform', open && 'rotate-180')}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[18rem] p-0" side="bottom" sideOffset={4}>
        <div className="py-1">
          {(['no_photo', 'no_email', 'no_phone'] as const).map((key) => (
            <label
              key={key}
              className="flex cursor-pointer items-center gap-2.5 px-3 py-1.5 hover:bg-muted/50"
            >
              <Checkbox
                checked={selectedKeys.includes(key)}
                onChange={() => onToggle(key)}
              />
              <span className="flex-1 text-sm text-foreground">
                {t(`members.filter.warnings.${key}`)}
              </span>
            </label>
          ))}
        </div>
        {selectedKeys.length > 0 && (
          <div className="border-t border-border px-3 py-2">
            <button
              type="button"
              onClick={onClearAll}
              className="font-heading text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none"
            >
              {t('members.filter.clearSection')}
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// Searchable combobox for teams — Radix Popover + inline search input.
function TeamsCombobox({
  teams,
  loading,
  selectedIds,
  onToggle,
  onClearAll,
}: {
  teams: { id: string; name: string; color: string; member_count: number }[];
  loading: boolean;
  selectedIds: string[];
  onToggle: (id: string) => void;
  onClearAll: () => void;
}) {
  const { t } = useTranslation('common');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return teams;
    return teams.filter((tm) => tm.name.toLowerCase().includes(q));
  }, [teams, search]);

  const label =
    selectedIds.length === 0
      ? t('members.filter.teams.all')
      : t('members.filter.teams.selected', { count: selectedIds.length });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex w-full items-center justify-between rounded-lg border px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            selectedIds.length > 0
              ? 'border-primary bg-primary/10 font-heading font-semibold text-foreground'
              : 'border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground',
          )}
        >
          <span>{loading ? t('members.filter.loading') : label}</span>
          <CaretDown
            className={cn('h-3.5 w-3.5 shrink-0 transition-transform', open && 'rotate-180')}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[18rem] p-0" side="bottom" sideOffset={4}>
        {/* Search */}
        <div className="relative border-b border-border px-3 py-2">
          <MagnifyingGlass className="pointer-events-none absolute left-5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('members.filter.teams.search')}
            className={cn(
              'w-full bg-transparent py-0.5 pl-6 text-sm placeholder:text-muted-foreground focus:outline-none',
              search.length > 0 && 'pr-6',
            )}
          />
          {search.length > 0 && (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label={t('common.clearSearch')}
              className="absolute right-4 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X weight="bold" className="h-3 w-3" />
            </button>
          )}
        </div>
        {/* List */}
        <div className="max-h-56 overflow-y-auto py-1">
          {filtered.length === 0 && (
            <p className="px-3 py-2 text-sm text-muted-foreground">
              {t('members.filter.teams.none')}
            </p>
          )}
          {filtered.map((team) => {
            const color = resolveTeamColor(team.name, team.color).dot;
            return (
              <label
                key={team.id}
                className="flex cursor-pointer items-center gap-2.5 px-3 py-1.5 hover:bg-muted/50"
              >
                <Checkbox
                  checked={selectedIds.includes(team.id)}
                  onChange={() => onToggle(team.id)}
                />
                <span
                  className="h-2 w-2 shrink-0 rounded-pill"
                  style={{ backgroundColor: color }}
                  aria-hidden
                />
                <span className="flex-1 truncate text-sm text-foreground">{team.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{team.member_count}</span>
              </label>
            );
          })}
        </div>
        {selectedIds.length > 0 && (
          <div className="border-t border-border px-3 py-2">
            <button
              type="button"
              onClick={onClearAll}
              className="font-heading text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none"
            >
              {t('members.filter.clearSection')}
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
